import { clearToken, getToken } from './auth'

const API_BASE = import.meta.env.VITE_API_BASE || 'http://localhost:8000'

export class ApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.status = status
  }
}

export async function api(path: string, options: RequestInit = {}) {
  const token = getToken()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  }
  if (token) headers['Authorization'] = `Bearer ${token}`

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers })

  if (res.status === 401) {
    clearToken()
  }
  if (!res.ok) {
    const text = await res.text()
    throw new ApiError(res.status, text || res.statusText)
  }
  if (res.status === 204) return null
  return res.json()
}

/** For multipart/form-data uploads — api() always sets Content-Type: json,
 * which breaks FormData bodies (the browser must set its own boundary). */
export async function apiUpload(path: string, formData: FormData) {
  const token = getToken()
  const headers: Record<string, string> = {}
  if (token) headers['Authorization'] = `Bearer ${token}`

  const res = await fetch(`${API_BASE}${path}`, { method: 'POST', headers, body: formData })

  if (res.status === 401) clearToken()
  if (!res.ok) throw new ApiError(res.status, (await res.text()) || res.statusText)
  return res.json()
}

/** For downloading files that require auth (e.g. credentialing documents) —
 * a plain <a href> can't attach the Authorization header, so fetch the bytes
 * as a blob and let the caller open/save it (e.g. via URL.createObjectURL). */
export async function apiBlob(path: string): Promise<Blob> {
  const token = getToken()
  const headers: Record<string, string> = {}
  if (token) headers['Authorization'] = `Bearer ${token}`

  const res = await fetch(`${API_BASE}${path}`, { headers })

  if (res.status === 401) clearToken()
  if (!res.ok) throw new ApiError(res.status, (await res.text()) || res.statusText)
  return res.blob()
}

export interface LoginResult {
  access_token: string | null
  token_type: string
  mfa_required: boolean
  mfa_challenge_token: string | null
}

export async function login(email: string, password: string): Promise<LoginResult> {
  const res = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  })
  if (!res.ok) throw new ApiError(res.status, await res.text())
  return res.json()
}

/** Second step of login when `login()` above reports mfa_required — exchanges
 * the short-lived challenge token plus a fresh TOTP code (or a recovery code)
 * for a real access token. Same LoginResult shape, this time with a token. */
export async function loginMfa(payload: { mfa_challenge_token: string; code?: string; recovery_code?: string }): Promise<LoginResult> {
  const res = await fetch(`${API_BASE}/auth/login/mfa`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!res.ok) throw new ApiError(res.status, await res.text())
  return res.json()
}
